import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Box, Typography, FormHelperText } from "@mui/material";
import { intl, LanguageBaseContext } from "dmsbasereact";

const DEFAULT_TOOLBAR = [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["clean"],
];

const q = (text) => `'"${text}"'`;

const getTranslationOverrides = () => ({
    "& .ql-snow .ql-picker.ql-header .ql-picker-label::before, & .ql-snow .ql-picker.ql-header .ql-picker-item::before": {
        content: q(intl.get("RTE.Normal").d("Normal")),
    },
    '& .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="1"]::before, & .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="1"]::before': {
        content: q(intl.get("RTE.Heading1").d("Heading 1")),
    },
    '& .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="2"]::before, & .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="2"]::before': {
        content: q(intl.get("RTE.Heading2").d("Heading 2")),
    },
    '& .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="3"]::before, & .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="3"]::before': {
        content: q(intl.get("RTE.Heading3").d("Heading 3")),
    },
    "& .ql-snow .ql-tooltip::before": {
        content: q(intl.get("RTE.VisitURL").d("Visit URL") + ":"),
    },
    "& .ql-snow .ql-tooltip a.ql-action::after": {
        content: q(intl.get("RTE.Edit").d("Edit")),
    },
    "& .ql-snow .ql-tooltip a.ql-remove::before": {
        content: q(intl.get("RTE.Remove").d("Remove")),
    },
    "& .ql-snow .ql-tooltip[data-mode='link']::before": {
        content: q(intl.get("RTE.EnterLink").d("Enter link") + ":"),
    },
});

export const DmsRichTextEditor = ({
    value,
    onChange,
    label,
    placeholder,
    toolbar,
    disabled = false,
    error = false,
    helperText,
    minHeight = 150,
    maxHeight = 400,
}) => {
    const { language } = useContext(LanguageBaseContext);

    const modules = useMemo(() => ({
        toolbar: toolbar || DEFAULT_TOOLBAR,
    }), [toolbar]);

    const translationSx = useMemo(() => getTranslationOverrides(), [language]);

    return (
        <Box>
            {label && (
                <Typography
                    variant="body2"
                    color={error ? "error" : "text.secondary"}
                    sx={{ mb: 0.5, fontWeight: 500 }}
                >
                    {label}
                </Typography>
            )}
            <Box
                sx={{
                    "& .ql-container": {
                        minHeight,
                        fontSize: "0.875rem",
                        fontFamily: "inherit",
                        borderBottomLeftRadius: 4,
                        borderBottomRightRadius: 4,
                        borderColor: error ? "error.main" : "rgba(0, 0, 0, 0.23)",
                    },
                    "& .ql-toolbar": {
                        borderTopLeftRadius: 4,
                        borderTopRightRadius: 4,
                        borderColor: error ? "error.main" : "rgba(0, 0, 0, 0.23)",
                        backgroundColor: disabled ? "action.disabledBackground" : "grey.50",
                    },
                    "& .ql-editor": {
                        minHeight,
                        maxHeight,
                        overflowY: "auto",
                        fontFamily: "inherit",
                    },
                    "& .ql-editor.ql-blank::before": {
                        fontStyle: "normal",
                        color: "text.disabled",
                    },
                    "& .ql-disabled .ql-editor": {
                        backgroundColor: "action.disabledBackground",
                        color: "text.disabled",
                    },
                    pointerEvents: disabled ? "none" : "auto",
                    opacity: disabled ? 0.6 : 1,
                    ...translationSx,
                }}
            >
                <ReactQuill
                    value={value}
                    onChange={onChange}
                    modules={modules}
                    placeholder={placeholder}
                    readOnly={disabled}
                    theme="snow"
                />
            </Box>
            {helperText && (
                <FormHelperText error={error}>{helperText}</FormHelperText>
            )}
        </Box>
    );
};

DmsRichTextEditor.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    toolbar: PropTypes.array,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    helperText: PropTypes.string,
    minHeight: PropTypes.number,
    maxHeight: PropTypes.number,
};
